/**
 * Shared `<img>` src state for team crests / player headshots that resolve against a
 * list of candidate CDN URLs.
 *
 * Renders the first candidate optimistically so the common case (exact-name asset
 * exists) paints with no swap, while an off-DOM probe confirms in parallel. When the
 * optimistic guess 404s the image is hidden rather than swapped to the fallback —
 * otherwise names whose asset lives under a later candidate (e.g. "South Africa
 * Under-19s", which only has the parent `south-africa.png`) visibly flash the default
 * shield before settling on the real crest.
 */

import { useCallback, useEffect, useState } from "react";
import {
  getCachedResolvedImageUrl,
  resolveFirstLoadingImageUrl,
} from "./resolveFirstLoadingImageUrl";

type ResolvedImageState = {
  src: string;
  /** The probe has finished, so `src` is the real result rather than a guess. */
  confirmed: boolean;
  /** The current `src` failed to load. */
  failed: boolean;
};

function firstCandidate(urls: readonly string[]): string {
  return (urls || []).find(Boolean) || "";
}

function initialState(
  urls: readonly string[],
  fallbackUrl: string,
): ResolvedImageState {
  const cached = getCachedResolvedImageUrl(urls);
  if (cached !== undefined) {
    return { src: cached || fallbackUrl, confirmed: true, failed: false };
  }
  return { src: firstCandidate(urls) || fallbackUrl, confirmed: false, failed: false };
}

export type UseResolvedImageSrc = {
  src: string;
  /** True while an unconfirmed guess has failed — keep the image invisible, not fallen back. */
  hidden: boolean;
  onError: () => void;
};

/**
 * @param candidateUrls Ordered URLs to try. Memoize by the caller — it drives the effect.
 * @param fallbackUrl Placeholder used once every candidate is known to have failed.
 */
export function useResolvedImageSrc(
  candidateUrls: readonly string[],
  fallbackUrl: string,
): UseResolvedImageSrc {
  const [state, setState] = useState<ResolvedImageState>(() =>
    initialState(candidateUrls, fallbackUrl),
  );

  useEffect(() => {
    let cancelled = false;

    const cached = getCachedResolvedImageUrl(candidateUrls);
    if (cached !== undefined) {
      setState({ src: cached || fallbackUrl, confirmed: true, failed: false });
      return undefined;
    }

    setState({ src: firstCandidate(candidateUrls), confirmed: false, failed: false });

    resolveFirstLoadingImageUrl(candidateUrls).then((resolved) => {
      if (cancelled) return;
      setState({ src: resolved || fallbackUrl, confirmed: true, failed: false });
    });

    return () => {
      cancelled = true;
    };
  }, [candidateUrls, fallbackUrl]);

  const onError = useCallback(() => {
    setState((prev) => {
      if (!prev.confirmed) {
        return prev.failed ? prev : { ...prev, failed: true };
      }
      if (prev.src === fallbackUrl) return prev;
      return { src: fallbackUrl, confirmed: true, failed: false };
    });
  }, [fallbackUrl]);

  return { src: state.src, hidden: state.failed && !state.confirmed, onError };
}
